import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ExternalLink, FileText, BookOpen, Video, Download } from "lucide-react";
import { useNavigate } from "react-router-dom";

export default function Resources() {
  const navigate = useNavigate();
  
  const resourceCategories = [
    {
      title: "NCAA Resources",
      icon: <FileText className="h-6 w-6" />,
      resources: [
        {
          name: "NCAA NIL Policy Overview",
          description: "Official NCAA interim name, image, and likeness policy",
          url: "https://ncaa.org/nil",
        },
        {
          name: "NCAA Compliance Guidelines",
          description: "Detailed compliance requirements for NIL activities",
          url: "https://ncaa.org/compliance",
        },
        {
          name: "Division I Manual",
          description: "Complete NCAA Division I manual with NIL provisions",
          url: "https://ncaa.org/d1manual",
        },
      ],
    },
    {
      title: "State Legislation",
      icon: <BookOpen className="h-6 w-6" />,
      resources: [
        {
          name: "State NIL Laws Database",
          description: "Comprehensive database of state-specific NIL legislation",
          url: "#",
        },
        {
          name: "Multi-State Comparison Tool",
          description: "Compare NIL laws across different states",
          url: "#",
        },
      ],
    },
    {
      title: "Title IX Resources",
      icon: <Video className="h-6 w-6" />,
      resources: [
        {
          name: "Title IX & NIL: Best Practices",
          description: "Guidance on maintaining gender equity in NIL programs",
          url: "#",
        },
        {
          name: "CAPS Revenue Sharing Guide",
          description: "Understanding the College Athlete Prosperity System",
          url: "#",
        },
        {
          name: "Equity Monitoring Tools",
          description: "Tools and templates for Title IX compliance tracking",
          url: "#",
        },
      ],
    },
    {
      title: "Templates & Forms",
      icon: <Download className="h-6 w-6" />,
      resources: [
        {
          name: "NIL Deal Disclosure Form",
          description: "Standard template for athlete deal disclosures",
          url: "#",
          downloadable: true,
        },
        {
          name: "Compliance Checklist",
          description: "Pre-approval checklist for NIL deals",
          url: "#",
          downloadable: true,
        },
        {
          name: "Tax Documentation Template",
          description: "Sample 1099 preparation and record-keeping templates",
          url: "#",
          downloadable: true,
        },
      ],
    },
  ];

  const quickLinks = [
    { name: "NIL Go Portal", url: "https://www.inflcr.com/" },
    { name: "Opendorse Platform", url: "https://opendorse.com/" },
    { name: "NCSA Athlete Portal", url: "https://www.ncsasports.org/" },
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation Header */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-background/95 backdrop-blur-sm border-b border-border/50">
        <div className="max-w-[1400px] mx-auto px-8 h-16 flex items-center justify-between">
          <button
            onClick={() => navigate("/")}
            className="text-sm font-light tracking-wide text-foreground hover:opacity-70 transition-opacity"
          >
            AthleteNexus
          </button>
          
          <div className="flex items-center gap-8">
            <button 
              onClick={() => navigate("/pricing")}
              className="text-sm font-light text-muted-foreground hover:text-foreground transition-colors"
            >
              Pricing
            </button>
            <button 
              onClick={() => navigate("/resources")}
              className="text-sm font-light text-muted-foreground hover:text-foreground transition-colors"
            >
              Resources
            </button>
            <button 
              onClick={() => navigate("/auth")}
              className="text-sm font-light text-muted-foreground hover:text-foreground transition-colors"
            >
              Sign In
            </button>
          </div>
        </div>
      </nav>

      <div className="container mx-auto px-4 pt-24 pb-16">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold mb-4">NIL Resources</h1>
            <p className="text-xl text-muted-foreground">
              Essential tools, templates, and reference materials for NIL compliance
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 mb-12">
            {resourceCategories.map((category, index) => (
              <Card key={index}>
                <CardHeader>
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-primary/10">
                      {category.icon}
                    </div>
                    <CardTitle>{category.title}</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {category.resources.map((resource, resourceIndex) => (
                      <div key={resourceIndex} className="border rounded-lg p-4 hover:bg-muted/50 transition-colors">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1">
                            <h3 className="font-medium mb-1">{resource.name}</h3>
                            <p className="text-sm text-muted-foreground">{resource.description}</p>
                          </div>
                          <Button variant="ghost" size="icon" asChild>
                            <a href={resource.url} target="_blank" rel="noopener noreferrer">
                              {(resource as any).downloadable ? (
                                <Download className="h-4 w-4" />
                              ) : (
                                <ExternalLink className="h-4 w-4" />
                              )}
                            </a>
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Quick Links</CardTitle>
              <CardDescription>External platforms and resources</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-3 gap-4">
                {quickLinks.map((link, index) => (
                  <Button key={index} variant="outline" className="h-auto py-4" asChild>
                    <a href={link.url} target="_blank" rel="noopener noreferrer" className="flex items-center justify-between">
                      <span>{link.name}</span>
                      <ExternalLink className="h-4 w-4 ml-2" />
                    </a>
                  </Button>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card className="mt-8 bg-primary/5 border-primary/20">
            <CardHeader>
              <CardTitle>Request Custom Resources</CardTitle>
              <CardDescription>
                Need specific templates or guidance for your institution?
              </CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground mb-4">
                Contact our team to request custom compliance templates, training materials,
                or institutional-specific resources tailored to your needs.
              </p>
              <Button>Contact Support</Button>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Footer */}
      <footer className="border-t border-border/50 bg-background/50 backdrop-blur-sm mt-24">
        <div className="max-w-[1400px] mx-auto px-8 py-12">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex items-center gap-3">
              <p className="text-xs font-light text-muted-foreground">
                © 2025 AthleteNexus. All rights reserved.
              </p>
              <div className="h-4 w-px bg-border/50" />
              <div className="flex items-center gap-2 px-2 py-1 rounded-md bg-muted/30 border border-border/30">
                <div className="h-2 w-2 rounded-full bg-green-500/80" />
                <span className="text-xs font-light text-muted-foreground">SOC 2 Compliant</span>
              </div>
            </div>
            
            <div className="flex items-center gap-6">
              <button className="text-xs font-light text-muted-foreground hover:text-foreground transition-colors">
                Documentation
              </button>
              <button className="text-xs font-light text-muted-foreground hover:text-foreground transition-colors">
                Support
              </button>
              <button className="text-xs font-light text-muted-foreground hover:text-foreground transition-colors">
                Privacy
              </button>
              <button className="text-xs font-light text-muted-foreground hover:text-foreground transition-colors">
                Terms
              </button>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
